import { useEffect, useRef, useState } from 'react';
import type { LivesStatus } from '../services/api';

/**
 * Countdown to nextRegenerationAt using serverTimeUtc to correct client clock drift (PRD §5.4).
 * Returns null when no timer (max lives, Admin unlimited, or missing data).
 */
export function useServerLivesCountdown(
  livesStatus: LivesStatus | null | undefined,
  onElapsed?: () => void
): string | null {
  const [label, setLabel] = useState<string | null>(null);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    if (!livesStatus?.nextRegenerationAt || livesStatus.serverTimeUtc == null) {
      setLabel(null);
      return;
    }

    const serverAtFetch = Date.parse(livesStatus.serverTimeUtc);
    const nextAt = Date.parse(livesStatus.nextRegenerationAt);
    if (Number.isNaN(serverAtFetch) || Number.isNaN(nextAt)) {
      setLabel(null);
      return;
    }

    // Anchor client clock to server time once; each tick advances "server now" by elapsed real time.
    const clientAtFetchMs = Date.now();

    let elapsedCalled = false;
    const tick = () => {
      const estimatedServerNowMs = serverAtFetch + (Date.now() - clientAtFetchMs);
      const remainingSec = Math.max(
        0,
        Math.floor((nextAt - estimatedServerNowMs) / 1000)
      );
      if (remainingSec <= 0) {
        setLabel(formatDuration(0));
        if (!elapsedCalled && onElapsedRef.current) {
          elapsedCalled = true;
          onElapsedRef.current();
        }
        return;
      }
      setLabel(formatDuration(remainingSec));
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [
    livesStatus?.serverTimeUtc,
    livesStatus?.nextRegenerationAt,
    livesStatus?.lives,
    livesStatus?.maxLives,
  ]);

  return label;
}

function formatDuration(totalSec: number): string {
  if (totalSec <= 0) return '0:00';
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}
